import {mutationTypes} from '#/notes/store/mutationTypes'
import {actionTypes} from '#/notes/store/actionTypes'
import {
  cloneDeep as _cloneDeep,
  omitBy as _omitBy,
  pickBy as _pickBy,
  isEmpty as _isEmpty
} from 'lodash'
import {defaultPagination} from '#/notes/components/definitions'
import {hasConflictedFilterValues, populateCustomDataFilters} from '#/notes/utilities/filters'

const actions = {

  async [actionTypes.FETCH_NOTES] ({commit, getters, state}, context = {}) {
    // Reset notes, if required
    let reset = true
    if (context.reset !== undefined) {
      reset = context.reset
    }

    // Prepare reference filters and user-defined filters
    const fallbackFilterFunction = ({userFilters}) => userFilters
    const referenceFilters = context.referenceFilters || {}
    const customFilterFunction = context.customFilterFunction || fallbackFilterFunction
    let {search, userFilters, pagination} = getters.noteListingQueries

    // Process custom data-related filters, if any
    userFilters = customFilterFunction({userFilters: _cloneDeep(userFilters)})

    if (hasConflictedFilterValues(userFilters, state.consumerFilters)) {
      // No note returned if we have one or more conflicted filter value
      // between consumer filters and user-defined filters
      commit(mutationTypes.UPDATE_PAGINATION, {
        total: 0,
        currentPage: 1,
        nextPage: null,
        pageSize: 10,
        previousPage: null
      })
      commit(mutationTypes.SET_NOTES, [])
      return
    }

    // Prepare custom data filter for API call
    const customDataFilters = populateCustomDataFilters(userFilters, state.consumerFilters)

    // Prepare filter for API call
    const filters = {
      ...userFilters,
      ...referenceFilters,
      ...state.consumerFilters,
      ...customDataFilters
    }

    // Prepare query for API call
    const queries = {
      search,
      filters,
      pagination
    }

    // Perform API call
    commit(mutationTypes.TOGGLE_IS_FETCHING, true)
    let response
    try {
      response = await getters.notesRepository.listRecords({queries})
    } finally {
      commit(mutationTypes.TOGGLE_IS_FETCHING, false)
    }
    commit(mutationTypes.UPDATE_PAGINATION, response.meta.pagination)
    if (reset) {
      commit(mutationTypes.SET_NOTES, response.data)
    } else {
      commit(mutationTypes.APPEND_NOTES, response.data)
    }
  },

  async [actionTypes.FETCH_NOTE_BY_ID] ({state, getters, commit}, id) {
    let note = state.noteRecords.find(note => note.id === id)
    if (!note) {
      const response = await getters.notesRepository.readRecord({id})
      note = response.data
    }
    commit(mutationTypes.UPDATE_NOTE, note)
    commit(mutationTypes.SET_CURRENT_NOTE, note)
  },

  async [actionTypes.FETCH_SORTABLE_NOTES] ({commit, getters}, queries = {}) {
    const _queries = {
      pagination: queries.pagination,
      search: queries.search,
      sort: queries.sort
    }
    return await getters.notesRepository.listRecords({queries: _queries})
  },

  async [actionTypes.UPDATE_USER_FILTERS] ({commit}, filters) {
    const isEmpty = value => _isEmpty(value)
    commit(mutationTypes.REMOVE_USER_FILTERS, _pickBy(filters, isEmpty))
    commit(mutationTypes.UPDATE_USER_FILTERS, _omitBy(filters, isEmpty))
  },

  async [actionTypes.DELETE_NOTE] ({commit, getters, dispatch}, {id, referenceFilters}) {
    commit(mutationTypes.TOGGLE_IS_FETCHING, true)
    try {
      await getters.notesRepository.deleteRecord({id})
    } finally {
      commit(mutationTypes.TOGGLE_IS_FETCHING, false)
    }
    commit(mutationTypes.UPDATE_PAGINATION, defaultPagination)
    dispatch(actionTypes.FETCH_NOTES, {referenceFilters})
  },

  async [actionTypes.ADD_NOTE] ({commit, getters}, note) {
    commit(mutationTypes.TOGGLE_IS_FETCHING, true)
    let response
    try {
      const reference = _cloneDeep(note.reference)
      reference.reference_domain = {id: getters.domainByName(note.reference.reference_domain).id}

      // Prepare reference id for note creation or update
      const {data} = await getters.referencesRepository.createOrGetRecord({data: reference})
      note.reference = {id: data.id}

      if (note.id) {
        response = await getters.notesRepository.updateRecord({id: note.id, data: note})
      } else {
        response = await getters.notesRepository.createRecord({data: note})
      }
    } finally {
      commit(mutationTypes.TOGGLE_IS_FETCHING, false)
    }
    commit(mutationTypes.UPDATE_NOTE, response.data)
  },

  async [actionTypes.REFRESH_NOTES] ({commit, dispatch}, context = {}) {
    commit(mutationTypes.SET_NOTES, [])
    commit(mutationTypes.UPDATE_PAGINATION, defaultPagination)
    dispatch(actionTypes.FETCH_NOTES, context)
  },

  async [actionTypes.FETCH_NOTE_TAGS] ({commit, getters}, queries = {}) {
    const _queries = {
      pagination: queries.pagination,
      search: queries.search
    }
    return await getters.noteTagsRepository.listRecords({queries: _queries})
  },

  async [actionTypes.FETCH_ALL_NOTE_TAGS] ({commit, getters}, queries = {}) {
    const _queries = {
      search: queries.search,
      sort: {key: 'name'},
      filters: {
        domain: queries.domain,
        active: queries.active
      }
    }
    const {data} = await getters.noteTagsRepository.listAllRecords({queries: _queries})
    commit(mutationTypes.SET_NOTE_TAGS, data)
  },

  async [actionTypes.ADD_NOTE_TAG] ({commit, getters}, noteTag) {
    return await getters.noteTagsRepository.createRecord({data: noteTag})
  },

  async [actionTypes.UPDATE_NOTE_TAG] ({commit, getters}, noteTag) {
    return await getters.noteTagsRepository.updateRecord({id: noteTag.id, data: noteTag})
  },

  async [actionTypes.FETCH_NOTE_TYPES] ({commit, getters}, queries = {}) {
    const _queries = {
      pagination: queries.pagination,
      search: queries.search
    }
    return await getters.noteTypesRepository.listRecords({queries: _queries})
  },

  async [actionTypes.FETCH_ALL_NOTE_TYPES] ({commit, getters}, queries = {}) {
    const _queries = {
      search: queries.search,
      sort: {key: 'name'},
      filters: {
        domain: queries.domain,
        active: queries.active
      }
    }
    const {data} = await getters.noteTypesRepository.listAllRecords({queries: _queries})
    commit(mutationTypes.SET_NOTE_TYPES, data)
  },

  async [actionTypes.ADD_NOTE_TYPE] ({commit, getters}, noteType) {
    return await getters.noteTypesRepository.createRecord({data: noteType})
  },

  async [actionTypes.UPDATE_NOTE_TYPE] ({commit, getters}, noteType) {
    return await getters.noteTypesRepository.updateRecord({id: noteType.id, data: noteType})
  },

  async [actionTypes.FETCH_NOTE_HISTORY] ({commit, getters}, id) {
    commit(mutationTypes.SET_CURRENT_NOTE_HISTORY, [])
    const response = await getters.notesRepository.listAllHistoryRecords({id})
    commit(mutationTypes.SET_CURRENT_NOTE_HISTORY, response.data)
  },

  [actionTypes.SWITCH_TO_MODE] ({commit}, {mode}) {
    commit(mutationTypes.SET_CURRENT_MODE, mode)
  },

  async [actionTypes.FETCH_ALL_DOMAINS] ({commit, getters}) {
    const {data} = await getters.domainsRepository.listAllRecords()
    commit(mutationTypes.SET_DOMAINS, data)
  }
}
export default actions
